/**
 * ProductionAnalysisTab — bám 1:1 sheet "Production Analysis" của workbook
 * `3. PRO-TLBL-25097 - BTA1 (Đắk Lắk).xlsm`, dùng style ĐÃ CHỐT ở tab BOM
 * (`/baoloc/report/batch-report/bom`): BlockTitle accent bar + bordered box +
 * ExField summary grid + NumText kế toán (số âm ngoặc tròn đỏ, tabular-nums).
 * Primitives chung ở [[reportUi]].
 *
 * Thứ tự section đúng sheet (A1 → A58):
 *   1. Overview (A1:F6 + side D1:E3) — BOM/PO/LIFFE/G2/mode + Input/Output/Source.
 *   2. INPUT  (A22:AM26) — Budget vs Actual grid (interactive, [[BudgetActualGrid]]).
 *   3. OUTPUT (A27:AM46) — Budget vs Actual grid (interactive).
 *   4. LOSS   (A48:L50) — summary grid.
 *   5. EXTRACTED VALUE (A52:H54) — summary grid.
 *   6. EXTRACTED VALUE vs. BOM (A56:F58) — summary grid.
 *
 * INPUT/OUTPUT áp full template list page (skill `/table-style`): global Search +
 * per-column ▽ filter + sort + pagination + Columns menu + freeze cột Code.
 * Number format bám đúng sheet ($#,##0 / $#,##0.0 / 0.00% / #,##0.000…).
 */
import type { ReactNode } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'

import { useGetBatchReportComparisonQuery, useGetBatchReportPersistQuery } from '../../../../store/api/factoryBomApi'
import type { ExtractedValueSummary, LossSummary } from '../productionReportMock'
import BudgetActualGrid from './BudgetActualGrid'
import NarrativeNotes from './NarrativeNotes'
import { fmtPct, fmtUsd } from './reportFormat'
import { BlockTitle, ExField, ExLabel, ExVal, NumText, PctText, QualityChip, RefNum } from './reportUi'
import { COLORS } from './styleTokens'
import { useProductionAnalysis } from './useProductionAnalysis'

/* ---------- 1. Overview (A1:F6 + side block) ---------------------------- */

interface OverviewData {
  bomCode: string
  poNumber: string
  liffe: number | null
  liffeAra: number | null
  diff: number | null
  diffAra: number | null
  mode: 'qui chuan' | 'chua qui chuan'
  inputCodes: string[]
  outputCodes: string[]
  source: string
}

function Overview({ ov }: { ov: OverviewData }) {
  return (
    <Box>
      <BlockTitle>Overview</BlockTitle>
      <Box
        sx={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: 1.5,
          bgcolor: COLORS.bgPage,
          p: 1.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
          columnGap: 7,
          rowGap: 1,
          alignItems: 'start',
        }}
      >
        {/* Trái: BOM Code · PO Number · Liffe · Diff · Source (Liffe=last, Diff=diff
            từ v_changeprice_batch, Robusta R13-ST1). Giá trị span cột 2→4. */}
        <Box
          sx={{
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: 'auto auto auto',
            columnGap: 1.5,
            rowGap: 0.4,
            gridAutoRows: 'minmax(22px, auto)',
            alignItems: 'center',
            justifyItems: 'start',
            justifyContent: 'start',
          }}
        >
          <ExLabel>BOM Code</ExLabel>
          <Box sx={{ gridColumn: '2 / 4' }}>
            {ov.bomCode ? (
              <Chip
                size="small"
                label={ov.bomCode}
                sx={{
                  height: 22,
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  color: '#fff',
                  bgcolor: COLORS.primary,
                  border: `1px solid ${COLORS.primary}`,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            ) : (
              <ExVal>—</ExVal>
            )}
          </Box>
          <ExLabel>PO Number</ExLabel>
          <Box sx={{ gridColumn: '2 / 4' }}><ExVal>{ov.poNumber || '—'}</ExVal></Box>
          <ExLabel>LIFFE</ExLabel>
          <Box sx={{ gridColumn: '2 / 4' }}><RefNum value={ov.liffe} /></Box>
          <ExLabel>Diffs</ExLabel>
          <Box sx={{ gridColumn: '2 / 4' }}><RefNum value={ov.diff} /></Box>
        </Box>

        {/* Phải: Input / Output (chips) / Mode. */}
        <Box
          sx={{
            minWidth: 0,
            alignSelf: 'start',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 1.5,
            rowGap: 0.4,
            gridAutoRows: 'minmax(22px, auto)',
            alignItems: 'center',
          }}
        >
          <Box sx={{ alignSelf: 'start', mt: '4px' }}><ExLabel>Input</ExLabel></Box>
          <CodeChips codes={ov.inputCodes} />
          <Box sx={{ alignSelf: 'start', mt: '4px' }}><ExLabel>Output</ExLabel></Box>
          <CodeChips codes={ov.outputCodes} />
          <Box sx={{ alignSelf: 'start', mt: '2px' }}><ExLabel>Mode</ExLabel></Box>
          <Box sx={{ justifySelf: 'start' }}>
            <Chip
              size="small"
              label={ov.mode === 'qui chuan' ? 'Standardized' : 'Non-standardized'}
              sx={{
                height: 22,
                fontSize: '0.72rem',
                fontWeight: 700,
                bgcolor: ov.mode === 'qui chuan' ? COLORS.successSoft : COLORS.bgHeader,
                color: ov.mode === 'qui chuan' ? COLORS.success : COLORS.textMuted,
                border: `1px solid ${ov.mode === 'qui chuan' ? COLORS.success : COLORS.border}`,
              }}
            />
          </Box>
          <Box sx={{ alignSelf: 'start', mt: '2px' }}><ExLabel>Source</ExLabel></Box>
          <Box sx={{ justifySelf: 'start' }}>
            <Typography sx={{ fontSize: '0.75rem', color: COLORS.textSubtle }}>{ov.source || '—'}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function CodeChips({ codes }: { codes: string[] }) {
  if (codes.length === 0) {
    return (
      <Box sx={{ justifySelf: 'start' }}>
        <Typography sx={{ fontSize: '0.75rem', color: COLORS.textSubtle }}>—</Typography>
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifySelf: 'start' }}>
      {codes.map((c) => (
        <QualityChip key={c} label={c} />
      ))}
    </Box>
  )
}

/* ---------- summary grid (LOSS / EXTRACTED VALUE) ----------------------- */

interface SummaryField {
  label: string
  value: ReactNode
  tone?: 'neutral' | 'warn' | 'danger' | 'success'
  strong?: boolean
}

/** Block summary kiểu BOM tab: bordered bgSubtle + grid ExField. */
function SummaryBlock({ title, fields, cols }: { title: string; fields: SummaryField[]; cols: number }) {
  return (
    <Box>
      <BlockTitle>{title}</BlockTitle>
      <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5, bgcolor: COLORS.bgSubtle, px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${cols}, 1fr)` },
            columnGap: 6,
            rowGap: 0.4,
          }}
        >
          {fields.map((f) => (
            <ExField key={f.label} label={f.label} value={f.value} tone={f.tone} strong={f.strong} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

function tone(n: number): SummaryField['tone'] {
  return n >= 0 ? 'success' : 'danger'
}

function LossBlock({ l }: { l: LossSummary }) {
  // A49/A50 — nhãn & format đúng sheet ("%" lặp lại → gắn rõ với chỉ tiêu).
  const fields: SummaryField[] = [
    { label: 'Total Loss', value: <><NumText n={l.totalLoss} d={3} /> Mt</> },
    { label: 'Total Loss %', value: <PctText frac={l.totalLossPct} /> },
    { label: 'MC In', value: <NumText n={l.mcIn} d={2} /> },
    { label: 'MC Out', value: <NumText n={l.mcOut} d={2} /> },
    { label: 'MC Loss', value: <NumText n={l.mcLoss} d={3} /> },
    { label: 'MC Loss %', value: <PctText frac={l.mcLossPct} /> },
    { label: 'Unexpl. Loss', value: <NumText n={l.unexpLoss} d={3} /> },
    { label: 'Unexpl. Loss %', value: <PctText frac={l.unexpLossPct} /> },
    { label: 'BOM Loss', value: <PctText frac={l.bomLoss} /> },
    { label: 'Var', value: fmtPct(l.variance * 100, 2), tone: tone(l.variance), strong: true },
  ]
  return <SummaryBlock title="Loss" fields={fields} cols={4} />
}

function ExtractedValueBlock({ e }: { e: ExtractedValueSummary }) {
  // A53/A54 — $#,##0 (In/Out/Extracted) + $#,##0.0 (/Mt, Handling, net).
  const fields: SummaryField[] = [
    { label: 'Value In', value: fmtUsd(e.valueIn, 0) },
    { label: 'Value Out', value: fmtUsd(e.valueOut, 0) },
    { label: 'Value Extracted', value: fmtUsd(e.valueExtracted, 0), tone: tone(e.valueExtracted), strong: true },
    { label: 'Value Extracted /Mt', value: fmtUsd(e.valueExtractedPerMt, 1) },
    { label: 'Handling', value: <NumText n={e.handling} d={1} usd /> },
    { label: 'Value Extracted /Mt -costs', value: fmtUsd(e.valueExtractedPerMtAfterCost, 1) },
  ]
  return <SummaryBlock title="Extracted value" fields={fields} cols={3} />
}

function ExtractedVsBomBlock({ e }: { e: ExtractedValueSummary }) {
  // A57/A58 — $#,##0.0 / $#,##0.00 / $#,##0 đúng sheet.
  const fields: SummaryField[] = [
    { label: 'Actual extracted', value: <NumText n={e.actualExtracted} d={1} usd /> },
    { label: 'BOM extracted', value: <NumText n={e.bomExtracted} d={2} usd /> },
    { label: 'P&L /Mt', value: fmtUsd(e.pnlPerMt, 2), tone: tone(e.pnlPerMt), strong: true },
    { label: 'Total P&L', value: fmtUsd(e.totalPnl, 0), tone: tone(e.totalPnl), strong: true },
  ]
  return <SummaryBlock title="Extracted value vs. BOM" fields={fields} cols={4} />
}

/* ---------- main tab ----------------------------------------------------- */

export interface ProductionAnalysisTabProps {
  factoryCode?: string
  /** PO No. đang chọn ở header → load Overview (LIFFE/Diff/Input/Output) theo PO. */
  poNo?: string
  /** BOM Code của PO (từ PO List) → query BOM detail. */
  bomCode?: string
  /** Ngày PO ISO → filter LIFFE/Diff theo v_changecost_quality. */
  poDateISO?: string
  /** Mode quy chuẩn (derive từ persist.is_batchstandard ở BatchReportPage). */
  mode?: 'qui chuan' | 'chua qui chuan'
  /** Quyền sửa Note/Standard (block Quality information). Chỉ phụ thuộc permission —
   *  KHÔNG khoá theo PO Finished (chị Đẹp: block này luôn cho sửa khi có quyền). */
  canEdit?: boolean
}

export default function ProductionAnalysisTab({ factoryCode, poNo, bomCode, poDateISO, mode, canEdit = false }: ProductionAnalysisTabProps) {
  // 100% data từ BE ([[useProductionAnalysis]] → endpoint /production-analysis):
  // INPUT/OUTPUT/Loss/Extracted tính từ 2 view + BOM recipe theo rule Excel. KHÔNG
  // còn mock — chưa chọn PO / PO chưa có stack → bảng rỗng + số 0.
  const pa = useProductionAnalysis({ factoryCode, bomCode, poNo, poDateISO })
  const bom = pa.bom
  // Narrative (Graded finished products + Note/Standard nhập tay) chuyển từ tab
  // Comparison sang đây — cùng nguồn comparison endpoint, KHÔNG cần chọn comparison
  // PO (narrative độc lập cmp_po). Persist po.compare_edit dùng chung.
  const { data: cmp, refetch: refetchCmp } = useGetBatchReportComparisonQuery(
    // Narrative độc lập, KHÔNG chọn comparison PO → comparisonPoNo '' (BE bỏ param).
    // skip guard dưới đảm bảo không gọi khi rỗng; `?? ''` chỉ để thoả type string.
    { factoryCode: factoryCode ?? '', poNo: poNo ?? '', comparisonPoNo: '' },
    { skip: !factoryCode || !poNo },
  )
  // Finished → khoá sửa Note/Standard (read-only sau khi PO đóng).
  const { data: persist } = useGetBatchReportPersistQuery(
    { factoryCode: factoryCode ?? '', poNo: poNo?.trim() ?? '' },
    { skip: !factoryCode || !poNo },
  )
  const editable = canEdit && !persist?.isFinished
  const ov: OverviewData = {
    bomCode: bomCode || '',
    poNumber: poNo?.trim() || '',
    liffe: bom.liffe,
    liffeAra: bom.liffeAra,
    diff: bom.diff,
    diffAra: bom.diffAra,
    mode: mode ?? 'chua qui chuan',
    // Overview chips theo từng PO — khớp BomTab Overview (Final>0, recipe ⊕ bom_edit).
    inputCodes: bom.overviewInputCodes,
    outputCodes: bom.overviewOutputCodes,
    source: pa.source,   // distinct origin_source ở INPUT (góp ý chị Đẹp)
  }

  return (
    <Stack spacing={2}>
      <Overview ov={ov} />
      <BudgetActualGrid title="Input — Budget vs Actual" rows={pa.inputRows} side="input" bomCodes={bom.inputCodes} storageKey="factory.production-analysis.input" />
      <BudgetActualGrid title="Output — Budget vs Actual" rows={pa.outputRows} side="output" bomCodes={bom.outputCodes} storageKey="factory.production-analysis.output" />
      <LossBlock l={pa.loss} />
      <ExtractedValueBlock e={pa.extracted} />
      <ExtractedVsBomBlock e={pa.extracted} />
      <NarrativeNotes
        rows={cmp?.narrative ?? []}
        factoryCode={factoryCode ?? ''}
        poNo={poNo?.trim() ?? ''}
        canEdit={editable}
        onSaved={() => void refetchCmp()}
      />
    </Stack>
  )
}
